import { useQuery } from '@tanstack/react-query';
import type {
  TimestampedConsoleLog,
  TimestampedNavigationEvent,
  TimestampedNetworkRequest,
} from '@chatsdet/types';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';

export type ExternalRunDiagnosticsDto = {
  consoleLogs: TimestampedConsoleLog[];
  networkRequests: TimestampedNetworkRequest[];
  navigationEvents: TimestampedNavigationEvent[];
  /** Echoes the item the payload was sliced to, or null for the whole run. */
  itemId: string | null;
  /** ISO time of the video's first frame, for absolute-time tooltips. */
  videoStartedAt: string | null;
};

/**
 * Console + network for an external run, optionally sliced to one test.
 *
 * Lazy (`enabled`) on purpose: a captured run's diagnostics are a separate S3
 * round trip, and most visits to a run page never open the panel. The API
 * returns timestamps already rebased onto the video's clock, so the player can
 * use them directly.
 */
export function useExternalRunDiagnostics(
  runId: string | null,
  itemId: string | null,
  enabled: boolean
) {
  return useQuery<ExternalRunDiagnosticsDto>({
    queryKey: ['external-run-diagnostics', runId, itemId],
    enabled: Boolean(runId) && enabled,
    // Artifacts are immutable once written; there is nothing to re-poll for.
    staleTime: Infinity,
    queryFn: async () => {
      const url = new URL(`${API_URL}/external-tests/runs/${runId}/diagnostics`);
      if (itemId) url.searchParams.set('itemId', itemId);
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load diagnostics');
      const body = (await res.json()) as { ok: boolean; data: ExternalRunDiagnosticsDto };
      return body.data;
    },
  });
}

/** Fetch only the small URL-waypoint artifact needed by the video address bar. */
export function useExternalRunNavigation(
  runId: string | null,
  itemId: string | null,
  enabled: boolean
) {
  return useQuery<ExternalRunDiagnosticsDto>({
    queryKey: ['external-run-navigation', runId, itemId],
    enabled: Boolean(runId) && Boolean(itemId) && enabled,
    staleTime: Infinity,
    queryFn: async () => {
      const url = new URL(`${API_URL}/external-tests/runs/${runId}/diagnostics`);
      if (itemId) url.searchParams.set('itemId', itemId);
      url.searchParams.set('mode', 'navigation');
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load navigation history');
      const body = (await res.json()) as { ok: boolean; data: ExternalRunDiagnosticsDto };
      return body.data;
    },
  });
}
